<article class="">
    <header class="">
        <div class="">
            <ul class="list-inline d-flex justify-content-between mb-0">
                <li class="list-inline-item">
                    <h1 class="">Geo-Hierarchy Methodology
                    </h1>
                </li>
                <li class="list-inline-item">
                    <button class="btn btn-icon btn-light-info mx-1" (click)="printPage()">
                        <i class="ti ti-printer"></i>
                    </button>
                    <button aria-label="Copy link" class="btn btn-icon btn-light-info" (click)="copyFullPageText()">
                        <i class="ti ti-copy"></i>
                    </button>
                </li>
            </ul>
        </div>
    </header>
    <div class="">
        <div class="card-body p-2">
            <p class="mt-1">The geographic hierarchy is the way by which Xeneta organizes ocean freight ports across the
                world into regional groupings based on their proximity and rate similarity.</p>
            <blockquote class="my-4 p-3 bg-yellow-100 color-block">
                <p class="m-0">Note that our entire geo-hierarchy for ocean freight ports is available for <a
                        href="https://www.xeneta.com/hubfs/static/ocean-geo-hierarchy.xlsx" target="_self">download in
                        Excel format</a> (65 KB).</p>
            </blockquote>
            <h2>Overview</h2>
            <p>Xeneta organizes individual ports into groups based on shared properties. Some groups are based
                on geographic proximity, while others are based on both proximity and price correlation.</p>
            <p>An individual port is at the lowest level of the geo-hierarchy, while the highest level is the
                continent that contains said port.</p>
            <p><span style="font-weight: 400;">For example, the geographical hierarchy for the port of
                    <strong>Oslo (NOOSL)</strong> in Xeneta looks like this:</span></p>
            <p>
                <span style="font-weight: 900;"><strong>World → Europe → Northern Europe → Scandinavia → Norway South
                        East → Oslo (NOOSL)</strong></span>
            </p>
            <p>When searching for rates, Xeneta always attempts to find direct contracted rates for your
                selected origin and destination pair.</p>
            <blockquote class="my-4 p-3 bg-gray-200 color-block">
                <p class="m-0">Note that you can browse through Xeneta's entire geo-hierarchy and manually
                    select your
                    origin and destination locations by clicking on the <span routerLink="/ports"
                        class="text-orange-700 cursor-pointer">Geo-Hierarchy</span>.</p>
            </blockquote>
            <p>If Xeneta is unable to find rates at a lower level in the geo-hierarchy, it will move one level
                up and attempt to find rates for a larger area. <span style="font-weight: 400;">If it cannot
                    find rates at the new level, it will continue to move further up until it reaches a
                    geographic level where it can provide a valid rate, or until it exhausts all available
                    options and returns no results.</span></p>
            <p><span style="font-weight: 400;">As Xeneta moves up the geo-hierarchy, it considers larger and
                    larger regions, and as a result it loses precision for its aggregated rates in favor of
                    being able to provide any rate.</span></p>
            <h2>Contracted and Regional Rates</h2>
            <p><span style="font-weight: 400;">Xeneta can display two types of rates in the search results on
                    the Market Overview page:</span></p>
            <ul>
                <li><span style="font-weight: 400;"><strong>Contracted rates</strong> — aggregated contract
                        rates between two specific ports</span></li>
                <li><span style="font-weight: 400;"><strong>Regional rates</strong> — aggregated rates between
                        larger regions when exact port-to-port rates are not available</span></li>
            </ul>
            <div class="">
                <span class="my-2">
                    <img src="../../../../../assets/img/marketMtrxList.png" alt="1.png"> <br>
                    <span class="fr-inner">Example of a regular contracted rate on Shanghai to Oslo </span>
                </span>
                <h2 class="my-2">Geo-Hierarchy Indicator</h2>
                <p>Most regional rates come with a geo-hierarchy indicator that shows how far up the geo-hierarchy
                    Xeneta had to move up to provide the displayed regional rate.</p>
            </div>
            <!-- <p><img src="https://cdn.elev.io/file/uploads/7rMuoX7QtXJ6psY2MyB2fXmaIgxnxoeXBP57N-qzYow/2zI6V7lYS4FQLkFbyw_9OxgVhv3bcDvM4-mcMOzp7Gc/regional-rate-icon-highlight-3X4.png"
                    alt="3.png" class="fr-fic fr-dii"></p>
            <p>With each step up the geo-hierarchy, the rate information becomes less precise, and the
                geo-hierarchy indicator changes color to reflect this.</p> -->
            <div class="table-wrapper">
                <table class="service-level-table" style="height: 150px;">
                    <tbody>
                        <tr>
                            <td style="width: 213px;"><strong>Color</strong></td>
                            <td style="width: 150px;" width="150"><strong>Precision</strong></td>
                            <td style="width: 215px;"><strong>Example</strong></td>
                        </tr>
                        <tr>
                            <td style="width: 213px;"><img
                                    src="https://cdn.elev.io/file/uploads/10Y3beWxAUhGtIJvObpkIHsd5znPcw-_TcXfr26CEVA/wDD6PhVs56l74CnUss-dq7rm93mhjHXsp6bia248JQQ/icon-green-7vQ.png"
                                    alt="icon-green.png" class="fr-fic fr-dii"></td>
                            <td style="width: 214px;">1 step up</td>
                            <td style="width: 215px;">
                                <p><span style="font-weight: 400;">Norway South East → Oslo</span></p>
                            </td>
                        </tr>
                        <tr>
                            <td style="width: 213px;"><img
                                    src="https://cdn.elev.io/file/uploads/kNoUUx4PLeoB8hMRnkvSEJHccC6jHDOLvxEEv9qc1gI/9FaNnTd5fSaxABUnnAP8OSUpFqEpzdAhdzlkbq0HAv0/icon-yellow-Mo8.png"
                                    alt="icon-yellow.png" class="fr-fic fr-dii"></td>
                            <td style="width: 214px;">2 steps up</td>
                            <td style="width: 215px;">
                                <p><span style="font-weight: 400;">Scandinavia → Norway South East → Oslo</span>
                                </p>
                            </td>
                        </tr>
                        <tr>
                            <td style="width: 213px;"><img
                                    src="https://cdn.elev.io/file/uploads/HLakmqNgEYowrr2JjzLJQnO-K3Shcdhc1LQyiotZW-A/Scfrv64Lucwn--mJhfSjgrmcEDRxoRC7l8jxnxT22jQ/icon-orange-SNQ.png"
                                    alt="icon-orange.png" class="fr-fic fr-dii"></td>
                            <td style="width: 214px;">3 steps up</td>
                            <td style="width: 215px;">
                                <p><span style="font-weight: 400;">Northern Europe → Scandinavia → Norway South
                                        East → Oslo</span></p>
                            </td>
                        </tr>
                        <tr>
                            <td style="width: 213px;"><img
                                    src="https://cdn.elev.io/file/uploads/u8gmNOUNfifY-FxTm8IpyZG4LOf9pjUDYpvP28yHrNU/oEee50pNw-7Uh448lUjhLg4_ciCak-Ua2JIminbDE50/icon-red-d94.png"
                                    alt="icon-red.png" class="fr-fic fr-dii"></td>
                            <td style="width: 214px;">Not enough data available</td>
                            <td style="width: 215px;">N/A</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <blockquote class="my-4 p-3 bg-gray-200 color-block">
                <p class="m-0">
                    Clicking on the indicator icon will open a dialog that explains how your selected origin and
                    destination regions were affected by moving up the geo-hierarchy.
                </p>
            </blockquote>
            <h3>Geo-Hierarchy Breakdown Example</h3>
            <p>In this example, we'll be looking at the <strong>Oslo–Hamburg</strong> trade lane.</p>
            <p>We can illustrate both ports using the geo-hierarchy approach — with the individual port at the
                bottom, followed by increasing regional scope with each step up, until the scope reaches the
                continent level:</p>
            <div>
                <table class="gh-table">
                    <tbody>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">World</span></p>
                            </td>
                            <td rowspan="6">
                                <p>&nbsp;</p>
                                <p>&nbsp;</p>
                                <p><span style="font-weight: 400;">→</span></p>
                                <p>&nbsp;</p>
                                <p>&nbsp;</p>
                            </td>
                            <td>&nbsp;</td>
                        </tr>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">Europe</span></p>
                            </td>
                            <td>
                                <p><span style="font-weight: 400;">World</span></p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">Northern Europe</span></p>
                            </td>
                            <td>
                                <p><span style="font-weight: 400;">Europe</span></p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">Scandinavia</span></p>
                            </td>
                            <td>
                                <p><span style="font-weight: 400;">Northern Europe</span></p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">Norway South East</span></p>
                            </td>
                            <td>
                                <p><span style="font-weight: 400;">North Europe Main</span></p>
                            </td>
                        </tr>
                        <tr>
                            <td class="gh-active">
                                <p>Oslo</p>
                            </td>
                            <td class="gh-active">
                                <p>Hamburg</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p>Both ports are situated in the <strong>Northern Europe</strong> region, but diverge into
                    distinct regions (<strong>Scandinavia</strong> and <strong>North Europe Main</strong>) as we
                    go one level down. By default, Xeneta will first look for contracted rates directly between
                    Oslo and Hamburg.</p>
                <p>If Xeneta does not have enough rate data to show the contracted rate between Oslo and
                    Hamburg, it will proceed to move one step up the geo-hierarchy. The system can move up the
                    hierarchy for either the origin or the destination, depending on data availability at the
                    time of request.</p>
                <table class="gh-table">
                    <tbody>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">World</span></p>
                            </td>
                            <td rowspan="6">
                                <p>&nbsp;</p>
                                <p>&nbsp;</p>
                                <p><span style="font-weight: 400;">→</span></p>
                                <p>&nbsp;</p>
                                <p>&nbsp;</p>
                            </td>
                            <td>&nbsp;</td>
                        </tr>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">Europe</span></p>
                            </td>
                            <td>
                                <p><span style="font-weight: 400;">World</span></p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">Northern Europe</span></p>
                            </td>
                            <td>
                                <p><span style="font-weight: 400;">Europe</span></p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">Scandinavia</span></p>
                            </td>
                            <td>
                                <p><span style="font-weight: 400;">Northern Europe</span></p>
                            </td>
                        </tr>
                        <tr>
                            <td class="gh-active">
                                <p>Norway South East</p>
                            </td>
                            <td class="gh-active">
                                <p>North Europe Main</p>
                            </td>
                        </tr>
                        <tr>
                            <td class="gh-active">
                                <p>Oslo</p>
                            </td>
                            <td class="gh-active">
                                <p>Hamburg</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p>One step up the geo-hierarchy will expand the potential candidates for regional rates to the
                    <strong>Norway South East</strong> and <strong>North Europe</strong> <strong>Main</strong>
                    regions. A single step up the geo-hierarchy (as indicated by<img class="mx-1"
                        src="https://cdn.elev.io/file/uploads/10Y3beWxAUhGtIJvObpkIHsd5znPcw-_TcXfr26CEVA/wDD6PhVs56l74CnUss-dq7rm93mhjHXsp6bia248JQQ/icon-green-7vQ.png"
                        alt="icon-green.png">in the application) will allow Xeneta to look at the following
                    regions in order to establish a regional rate:
                </p>
                <ul>
                    <li style="font-weight: bold;"><strong>Oslo — North Europe Main</strong></li>
                    <li style="font-weight: bold;"><strong>Norway South East — Hamburg</strong></li>
                </ul>
                <p>If a single step up the geo-hierarchy does not yield enough data to reliably show a regional
                    rate, the system will go two steps up the geo-hierarchy.</p>
                <table class="gh-table">
                    <tbody>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">World</span></p>
                            </td>
                            <td rowspan="6">
                                <p>&nbsp;</p>
                                <p>&nbsp;</p>
                                <p><span style="font-weight: 400;">→</span></p>
                                <p>&nbsp;</p>
                                <p>&nbsp;</p>
                            </td>
                            <td>&nbsp;</td>
                        </tr>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">Europe</span></p>
                            </td>
                            <td>
                                <p><span style="font-weight: 400;">World</span></p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p><span style="font-weight: 400;">Northern Europe</span></p>
                            </td>
                            <td>
                                <p><span style="font-weight: 400;">Europe</span></p>
                            </td>
                        </tr>
                        <tr>
                            <td class="gh-active">
                                <p>Scandinavia</p>
                            </td>
                            <td class="gh-active">
                                <p>Northern Europe</p>
                            </td>
                        </tr>
                        <tr>
                            <td class="gh-active">
                                <p>Norway South East</p>
                            </td>
                            <td class="gh-active">
                                <p>North Europe Main</p>
                            </td>
                        </tr>
                        <tr>
                            <td class="gh-active">
                                <p>Oslo</p>
                            </td>
                            <td class="gh-active">
                                <p>Hamburg</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p>Two steps up the geo-hierarchy (as indicated by<img class="mx-1"
                        src="https://cdn.elev.io/file/uploads/kNoUUx4PLeoB8hMRnkvSEJHccC6jHDOLvxEEv9qc1gI/9FaNnTd5fSaxABUnnAP8OSUpFqEpzdAhdzlkbq0HAv0/icon-yellow-Mo8.png"
                        alt="icon-yellow.png">in the application) will expand potential candidates for a
                    regional rate all the way up to the <strong>Scandinavia</strong> and <strong>Northern
                        Europe</strong> regions.</p>
                <p>As before, the system will be able to take two steps up the geo-hierarchy across both the
                    origin and the destination, meaning that Xeneta will look at the following regions in order
                    to establish a regional rate:</p>
                <ul>
                    <li style="font-weight: bold;"><strong>Norway South East — North Europe Main</strong></li>
                    <li style="font-weight: bold;"><strong>Oslo — Northern Europe</strong></li>
                    <li style="font-weight: bold;"><strong>Scandinavia — Hamburg</strong></li>
                </ul>
            </div>
            <p>This process will continue until Xeneta finds a level in the geo-hierarchy that can provide a
                valid freight rate or until it determines that it does not have enough data.</p>
            <blockquote class="my-4 p-3 bg-gray-200 color-block">
                <p class="m-0">Note that Xeneta does its best to always provide a contracted rate by default,
                    but it will
                    offer a regional rate if no contracted rates are available in the system for the selected
                    origin-destination pair.</p>
            </blockquote>
        </div>
    </div>
</article>